import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { motion } from 'motion/react';

import { useLayoutGoalReport } from '@queries';
import { layoutAtom } from '@store';
import { Box, Heading4, Heading2, Text, FilterIcon, u, BaseToggleButton, Stack, Divider, Above, Inline } from '@ui';

import { useGoalReportContext } from '../GoalReportContext';
import { getFilterDescription, getGoalDataIncluded, getReportPeriod } from '../goalReportUtils';

import * as css from './GoalReportHeadingGroup.css';

export const GoalReportHeadingGroup = () => {
    const { layoutId, showFilters, filters, customRange, toggleShowFilters } = useGoalReportContext();
    const layout = useAtomValue(layoutAtom);
    const { data: totalGoalReport } = useLayoutGoalReport({ layoutId });

    const swings = totalGoalReport?.[0].swings;
    const { legend: swingLegend, value: swingValue } = useMemo(() => getReportPeriod(swings), [swings]);
    const { legend: dataIncludedLegend, value: dataIncludedValue } = useMemo(
        () => getGoalDataIncluded(swings),
        [swings],
    );

    const goalName = layout?.name;
    return (
        <Box
            display="flex"
            flexDirection={{ xs: 'column', lg: 'row' }}
            mb={u(4)}
            gap={{ xs: u(4), lg: u(0.5) }}
            alignItems={{ md: 'flex-start', lg: 'center' }}
        >
            <Box flex={1}>
                <Heading2 whiteSpace={'nowrap'}>{goalName}</Heading2>
            </Box>
            <Inline gap={u(4)} width="100%" justifyContent={{ lg: 'flex-end' }}>
                <Stack>
                    <Heading4>{dataIncludedLegend}</Heading4>
                    <Text whiteSpace={'nowrap'} variant="pSmall">
                        {dataIncludedValue}
                    </Text>
                </Stack>
                <Stack>
                    <Heading4 whiteSpace="nowrap">{swingLegend}</Heading4>
                    <Text variant="pSmall" whiteSpace="nowrap">
                        {swingValue}
                    </Text>
                </Stack>
            </Inline>
            <Above bp={'md'} avoidRendering>
                <Box mx={u(2)} display={{ md: 'none', lg: 'block' }}>
                    <Divider orientation="vertical" length={32} />
                </Box>
            </Above>

            <Inline width="100%" flex={0} gap={0} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
                <Stack mr={u(2)} className={css.minWidth}>
                    <Inline alignItems="center" gap={u(0.5)}>
                        <Heading4 whiteSpace="nowrap" textAlign="right">
                            Filter Status
                        </Heading4>
                        <FilterIcon size={10} />
                    </Inline>
                    <Text variant="pSmall" whiteSpace="nowrap">
                        {getFilterDescription(filters, customRange)}
                    </Text>
                </Stack>
                <Inline width={'max-content'}>
                    <BaseToggleButton isActive={showFilters} onChange={toggleShowFilters} stroke>
                        <motion.span
                            animate={{ rotate: showFilters ? 180 : 0 }}
                            transition={{ duration: 0.2, ease: 'easeIn' }}
                            style={{ display: 'inline-flex' }}
                        >
                            <FilterIcon size={14} />
                        </motion.span>
                        filters
                    </BaseToggleButton>
                </Inline>
            </Inline>
        </Box>
    );
};
